import React, { useState, useEffect, useCallback } from 'react';
import { legacyApiService, LegacyEvent } from '../services/api-compat';
import { useAuth } from '../contexts/AuthContext';
import AdminInvite from './AdminInvite';
import './AdminDashboard.css';

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'events' | 'tickets' | 'users'>('events');
  const [events, setEvents] = useState<LegacyEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user, signOut } = useAuth();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const eventsData = await legacyApiService.getEvents();
      setEvents(eventsData);
    } catch (err) {
      setError('Failed to load events');
      console.error('Error loading events:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <div className="admin-header-actions">
          <span className="user-info">Welcome, {user?.email}</span>
          <button 
            onClick={() => signOut()} 
            className="logout-button"
          >
            Logout
          </button>
        </div>
      </div>
      
      <div className="dashboard-content">
        <div className="tab-navigation">
          <button 
            className={activeTab === 'events' ? 'active' : ''}
            onClick={() => setActiveTab('events')}
          >
            Events
          </button>
          <button 
            className={activeTab === 'tickets' ? 'active' : ''}
            onClick={() => setActiveTab('tickets')}
          >
            Tickets
          </button>
          <button 
            className={activeTab === 'users' ? 'active' : ''}
            onClick={() => setActiveTab('users')}
          >
            User Management
          </button>
        </div>
      </div>

      <div className="admin-content">
        {error && <div className="error-message">{error}</div>}
        
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <>
            {activeTab === 'events' && (
              <div className="events-section">
                <div className="section-header">
                  <h2>Events Management</h2>
                  <p>Use the <strong>Event Manager</strong> for creating and editing events with advanced features.</p>
                  <a href="/manager" className="btn btn-primary">
                    Go to Event Manager
                  </a>
                </div>

                <div className="events-table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Date</th>
                        <th>Location</th>
                        <th>Price</th>
                        <th>Capacity</th>
                        <th>Available</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((event) => (
                        <tr key={event.id}>
                          <td>{event.title}</td>
                          <td>{new Date(event.dateTime).toLocaleDateString()}</td>
                          <td>{event.location}</td>
                          <td>€{event.price.toFixed(2)}</td>
                          <td>{event.maxAttendees}</td>
                          <td>{event.availableTickets || event.maxAttendees}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {events.length === 0 && (
                    <div className="empty-state">
                      <p>No events found. Create your first event using the Event Manager.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'tickets' && (
              <div className="tickets-section">
                <div className="section-header">
                  <h2>Ticket Validation</h2>
                  <p>Validate tickets using the ticket validator.</p>
                  <a href="/validate" className="btn btn-primary">
                    Go to Ticket Validator
                  </a>
                </div>
                
                <div className="info-box">
                  <h3>Ticket Management Features:</h3>
                  <ul>
                    <li>Validate QR codes and ticket IDs</li>
                    <li>Check ticket status and usage</li>
                    <li>View ticket purchase history</li>
                    <li>Generate usage reports</li>
                  </ul>
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="users-section">
                <div className="section-header">
                  <h2>User Management</h2>
                  <p>Manage admin users and send invitations.</p>
                </div>
                
                <AdminInvite onInviteSent={(email) => {
                  console.log('Invitation sent to:', email)
                  // You could add a notification here
                }} />
                
                <div className="info-box">
                  <h3>User Management Features:</h3>
                  <ul>
                    <li>Invite new admin users</li>
                    <li>Manage user roles and permissions</li>
                    <li>View user activity logs</li>
                    <li>Deactivate or suspend accounts</li>
                  </ul>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
